import { createClient } from "@connectrpc/connect";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import {
	Check,
	FolderOpen,
	Pencil,
	Plus,
	Search,
	Tag as TagIcon,
	Trash2,
} from "lucide-react";
import { useState } from "react";

import { TagBadge } from "@/components/tags/TagBadge";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { TagService } from "@/gen/omnios/v1/tag_connect";
import type { Tag, TagGroup } from "@/gen/omnios/v1/tag_pb";
import { transport } from "@/lib/grpc";
import { useBreadcrumbs } from "@/lib/use-breadcrumbs";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute("/settings/tags")({
	component: TagSettingsPage,
});

const client = createClient(TagService, transport);

type TabId = "tags" | "groups";

function TagSettingsPage() {
	const { t } = useTranslation("settings");

	const TABS = [
		{ id: "tags" as const, label: t("tags_tab_tags"), icon: TagIcon },
		{ id: "groups" as const, label: t("tags_tab_groups"), icon: FolderOpen },
	];

	useBreadcrumbs([
		{ label: t("breadcrumb_settings"), href: "/settings" },
		{ label: t("tags_breadcrumb") },
	]);
	const [activeTab, setActiveTab] = useState<TabId>("tags");

	return (
		<div>
			<div className="-mx-6 -mt-4 flex border-b bg-muted/30">
				{TABS.map((tab) => (
					<Button
						key={tab.id}
						variant="ghost"
						size="sm"
						onClick={() => setActiveTab(tab.id)}
						className={`gap-1.5 rounded-none border-b-2 px-4 py-2.5 ${
							activeTab === tab.id
								? "border-primary text-primary"
								: "border-transparent text-muted-foreground hover:text-foreground"
						}`}
					>
						<tab.icon className="h-4 w-4" />
						{tab.label}
					</Button>
				))}
			</div>
			<div className="pt-5">
				{activeTab === "tags" && <TagsTab />}
				{activeTab === "groups" && <GroupsTab />}
			</div>
		</div>
	);
}

// ─── Tags Tab ───────────────────────────────────────

function TagsTab() {
	const { t } = useTranslation("settings");
	const qc = useQueryClient();
	const [showCreate, setShowCreate] = useState(false);
	const [editTag, setEditTag] = useState<Tag | null>(null);
	const [showArchived, setShowArchived] = useState(false);
	const [search, setSearch] = useState("");

	const { data: tagsResp } = useQuery({
		queryKey: ["tags", "list", showArchived],
		queryFn: () =>
			client.listTags({
				page: { pageSize: 500, pageToken: "" },
				includeArchived: showArchived,
			}),
	});

	const deleteMut = useMutation({
		mutationFn: (id: string) => client.deleteTag({ id }),
		onSuccess: () => qc.invalidateQueries({ queryKey: ["tags"] }),
	});

	const allTags = tagsResp?.tags ?? [];
	const tags = allTags.filter(
		(tag) =>
			!search ||
			tag.name.toLowerCase().includes(search.toLowerCase()) ||
			(tag.description ?? "").toLowerCase().includes(search.toLowerCase()),
	);

	return (
		<div className="space-y-4">
			<div className="flex items-center justify-between gap-4">
				<div className="relative">
					<Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
					<Input
						placeholder={t("tags_search_placeholder")}
						value={search}
						onChange={(e) => setSearch(e.target.value)}
						className="pl-9 h-9 w-56"
					/>
				</div>
				<div className="flex items-center gap-2">
					<Label className="flex items-center gap-2 text-muted-foreground">
						<Switch checked={showArchived} onCheckedChange={setShowArchived} />
						{t("tags_show_archived")}
					</Label>
					<Button size="sm" onClick={() => setShowCreate(true)}>
						<Plus className="mr-1 h-4 w-4" />
						{t("tags_create")}
					</Button>
				</div>
			</div>

			<div className="rounded-lg border overflow-hidden">
				<Table>
					<TableHeader>
						<TableRow className="bg-muted/30">
							<TableHead>{t("tags_col_tag")}</TableHead>
							<TableHead>{t("tags_col_namespace")}</TableHead>
							<TableHead>{t("tags_col_type")}</TableHead>
							<TableHead>{t("tags_col_group")}</TableHead>
							<TableHead className="text-right">
								{t("tags_col_usage")}
							</TableHead>
							<TableHead className="w-20" />
						</TableRow>
					</TableHeader>
					<TableBody>
						{tags.map((tag) => (
							<TableRow key={tag.id} className="hover:bg-muted/50">
								<TableCell>
									<TagBadge tag={tag} size="md" />
								</TableCell>
								<TableCell className="text-muted-foreground">
									{tag.namespace}
								</TableCell>
								<TableCell className="text-muted-foreground">
									{tag.key ? `${tag.key}:${tag.value}` : "—"}
								</TableCell>
								<TableCell className="text-muted-foreground">
									{tag.group?.name || "—"}
								</TableCell>
								<TableCell className="text-right tabular-nums">
									{tag.usageCount}
								</TableCell>
								<TableCell>
									<div className="flex items-center justify-end gap-1">
										<Button
											variant="ghost"
											size="icon"
											className="h-7 w-7"
											onClick={() => setEditTag(tag)}
										>
											<Pencil className="h-3.5 w-3.5" />
										</Button>
										<Button
											variant="ghost"
											size="icon"
											className="h-7 w-7 text-destructive"
											onClick={() => deleteMut.mutate(tag.id)}
										>
											<Trash2 className="h-3.5 w-3.5" />
										</Button>
									</div>
								</TableCell>
							</TableRow>
						))}
						{tags.length === 0 && (
							<TableRow>
								<TableCell
									colSpan={6}
									className="py-8 text-center text-muted-foreground"
								>
									{t("tags_empty")}
								</TableCell>
							</TableRow>
						)}
					</TableBody>
				</Table>
			</div>

			{(showCreate || editTag) && (
				<TagDialog
					tag={editTag}
					onClose={() => {
						setShowCreate(false);
						setEditTag(null);
					}}
				/>
			)}
		</div>
	);
}

// ─── Tag Create/Edit Dialog ─────────────────────────

function TagDialog({ tag, onClose }: { tag: Tag | null; onClose: () => void }) {
	const { t } = useTranslation("settings");
	const TAG_COLORS = [
		{ value: "red", label: t("tags_color_red") },
		{ value: "orange", label: t("tags_color_orange") },
		{ value: "yellow", label: t("tags_color_yellow") },
		{ value: "green", label: t("tags_color_green") },
		{ value: "blue", label: t("tags_color_blue") },
		{ value: "purple", label: t("tags_color_purple") },
		{ value: "pink", label: t("tags_color_pink") },
		{ value: "gray", label: t("tags_color_gray") },
	];
	const qc = useQueryClient();
	const [name, setName] = useState(tag?.name ?? "");
	const [color, setColor] = useState(tag?.color ?? "blue");
	const [icon, setIcon] = useState(tag?.icon ?? "");
	const [description, setDescription] = useState(tag?.description ?? "");
	const [kind, setKind] = useState(tag?.kind ?? "");
	const [key, setKey] = useState(tag?.key ?? "");
	const [value, setValue] = useState(tag?.value ?? "");

	const createMut = useMutation({
		mutationFn: () =>
			client.createTag({ name, color, icon, description, kind, key, value }),
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: ["tags"] });
			onClose();
		},
	});

	const updateMut = useMutation({
		mutationFn: () =>
			client.updateTag({ id: tag!.id, name, color, icon, description, kind }),
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: ["tags"] });
			onClose();
		},
	});

	const save = () => (tag ? updateMut.mutate() : createMut.mutate());
	const isPending = createMut.isPending || updateMut.isPending;

	return (
		<Dialog open onOpenChange={() => onClose()}>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>
						{tag ? t("tags_edit_title") : t("tags_create_title")}
					</DialogTitle>
				</DialogHeader>
				<div className="space-y-3 py-2">
					<div className="space-y-1">
						<Label>{t("tags_label_name")}</Label>
						<Input
							value={name}
							onChange={(e) => setName(e.target.value)}
							placeholder={t("tags_name_placeholder")}
						/>
					</div>
					<div className="grid grid-cols-2 gap-3">
						<div className="space-y-1">
							<Label>{t("tags_label_color")}</Label>
							<Select value={color} onValueChange={setColor}>
								<SelectTrigger>
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									{TAG_COLORS.map((c) => (
										<SelectItem key={c.value} value={c.value}>
											{c.label}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
						<div className="space-y-1">
							<Label>{t("tags_label_icon")}</Label>
							<Input
								value={icon}
								onChange={(e) => setIcon(e.target.value)}
								placeholder=""
							/>
						</div>
					</div>
					<div className="space-y-1">
						<Label>{t("tags_label_description")}</Label>
						<Input
							value={description}
							onChange={(e) => setDescription(e.target.value)}
							placeholder={t("tags_desc_placeholder")}
						/>
					</div>
					{!tag && (
						<div className="grid grid-cols-2 gap-3">
							<div className="space-y-1">
								<Label>{t("tags_label_key")}</Label>
								<Input
									value={key}
									onChange={(e) => setKey(e.target.value)}
									placeholder="env"
								/>
							</div>
							<div className="space-y-1">
								<Label>{t("tags_label_value")}</Label>
								<Input
									value={value}
									onChange={(e) => setValue(e.target.value)}
									placeholder="production"
								/>
							</div>
						</div>
					)}
					<div className="space-y-1">
						<Label>{t("tags_label_kind")}</Label>
						<Input
							value={kind}
							onChange={(e) => setKind(e.target.value)}
							placeholder={t("tags_kind_placeholder")}
						/>
					</div>
				</div>
				<DialogFooter>
					<Button variant="outline" onClick={onClose}>
						{t("common:cancel")}
					</Button>
					<Button onClick={save} disabled={!name.trim() || isPending}>
						{tag ? t("common:save") : t("common:create")}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}

// ─── Groups Tab ─────────────────────────────────────

function GroupsTab() {
	const { t } = useTranslation("settings");
	const qc = useQueryClient();
	const [showCreate, setShowCreate] = useState(false);
	const [editGroup, setEditGroup] = useState<TagGroup | null>(null);
	const [search, setSearch] = useState("");

	const { data: groupsResp } = useQuery({
		queryKey: ["tag-groups"],
		queryFn: () => client.listTagGroups({ includeTags: true }),
	});

	const deleteMut = useMutation({
		mutationFn: (id: string) => client.deleteTagGroup({ id }),
		onSuccess: () => qc.invalidateQueries({ queryKey: ["tag-groups"] }),
	});

	const allGroups = groupsResp?.groups ?? [];
	const groups = allGroups.filter(
		(g) =>
			!search ||
			g.name.toLowerCase().includes(search.toLowerCase()) ||
			g.description.toLowerCase().includes(search.toLowerCase()),
	);

	return (
		<div className="space-y-4">
			<div className="flex items-center justify-between gap-4">
				<div className="relative">
					<Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
					<Input
						placeholder={t("tags_groups_search_placeholder")}
						value={search}
						onChange={(e) => setSearch(e.target.value)}
						className="pl-9 h-9 w-56"
					/>
				</div>
				<Button size="sm" onClick={() => setShowCreate(true)}>
					<Plus className="mr-1 h-4 w-4" />
					{t("tags_groups_create")}
				</Button>
			</div>

			<div className="rounded-lg border overflow-hidden">
				<Table>
					<TableHeader>
						<TableRow className="bg-muted/30">
							<TableHead>{t("tags_groups_col_name")}</TableHead>
							<TableHead>{t("tags_groups_col_description")}</TableHead>
							<TableHead>{t("tags_groups_col_exclusive")}</TableHead>
							<TableHead>{t("tags_groups_col_tags")}</TableHead>
							<TableHead>{t("tags_groups_col_required")}</TableHead>
							<TableHead className="w-20" />
						</TableRow>
					</TableHeader>
					<TableBody>
						{groups.map((group) => (
							<TableRow key={group.id} className="hover:bg-muted/50">
								<TableCell className="font-medium">{group.name}</TableCell>
								<TableCell className="text-muted-foreground">
									{group.description || "—"}
								</TableCell>
								<TableCell>
									{group.exclusive && (
										<Check className="h-4 w-4 text-primary" />
									)}
								</TableCell>
								<TableCell>
									{group.tags.length > 0 ? (
										<div className="flex flex-wrap gap-1">
											{group.tags.map((tag) => (
												<TagBadge key={tag.id} tag={tag} />
											))}
										</div>
									) : (
										<span className="text-muted-foreground">—</span>
									)}
								</TableCell>
								<TableCell className="text-muted-foreground text-sm">
									{group.requiredFor.length > 0
										? group.requiredFor.join(", ")
										: "—"}
								</TableCell>
								<TableCell>
									<div className="flex items-center justify-end gap-1">
										<Button
											variant="ghost"
											size="icon"
											className="h-7 w-7"
											onClick={() => setEditGroup(group)}
										>
											<Pencil className="h-3.5 w-3.5" />
										</Button>
										<Button
											variant="ghost"
											size="icon"
											className="h-7 w-7 text-destructive"
											onClick={() => deleteMut.mutate(group.id)}
										>
											<Trash2 className="h-3.5 w-3.5" />
										</Button>
									</div>
								</TableCell>
							</TableRow>
						))}
						{groups.length === 0 && (
							<TableRow>
								<TableCell
									colSpan={6}
									className="py-8 text-center text-muted-foreground"
								>
									{t("tags_groups_empty")}
								</TableCell>
							</TableRow>
						)}
					</TableBody>
				</Table>
			</div>

			{(showCreate || editGroup) && (
				<GroupDialog
					group={editGroup}
					onClose={() => {
						setShowCreate(false);
						setEditGroup(null);
					}}
				/>
			)}
		</div>
	);
}

function GroupDialog({
	group,
	onClose,
}: { group: TagGroup | null; onClose: () => void }) {
	const { t } = useTranslation("settings");
	const qc = useQueryClient();
	const [name, setName] = useState(group?.name ?? "");
	const [description, setDescription] = useState(group?.description ?? "");
	const [color, _setColor] = useState(group?.color ?? "");
	const [exclusive, setExclusive] = useState(group?.exclusive ?? false);

	const createMut = useMutation({
		mutationFn: () =>
			client.createTagGroup({ name, description, color, exclusive }),
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: ["tag-groups"] });
			onClose();
		},
	});

	const updateMut = useMutation({
		mutationFn: () =>
			client.updateTagGroup({
				id: group!.id,
				name,
				description,
				color,
				exclusive,
				requiredFor: group!.requiredFor,
				sortOrder: group!.sortOrder,
			}),
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: ["tag-groups"] });
			onClose();
		},
	});

	const save = () => (group ? updateMut.mutate() : createMut.mutate());
	const isPending = createMut.isPending || updateMut.isPending;

	return (
		<Dialog open onOpenChange={() => onClose()}>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>
						{group
							? t("tags_groups_edit_title")
							: t("tags_groups_create_title")}
					</DialogTitle>
				</DialogHeader>
				<div className="space-y-3 py-2">
					<div className="space-y-1">
						<Label>{t("tags_groups_col_name")}</Label>
						<Input
							value={name}
							onChange={(e) => setName(e.target.value)}
							placeholder={t("tags_groups_name_placeholder")}
						/>
					</div>
					<div className="space-y-1">
						<Label>{t("tags_groups_col_description")}</Label>
						<Input
							value={description}
							onChange={(e) => setDescription(e.target.value)}
						/>
					</div>
					<div className="flex items-center gap-2">
						<Switch checked={exclusive} onCheckedChange={setExclusive} />
						<Label>{t("tags_groups_exclusive_hint")}</Label>
					</div>
				</div>
				<DialogFooter>
					<Button variant="outline" onClick={onClose}>
						{t("common:cancel")}
					</Button>
					<Button onClick={save} disabled={!name.trim() || isPending}>
						{group ? t("common:save") : t("common:create")}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
